import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter,
  AlertDialogHeader, AlertDialogOverlay, Box, Button, useDisclosure, useToast,
} from "@chakra-ui/react";
import { SideFilter, type FilterField } from "../../../components/SideFilter";
import { useDownloadManager } from "../../../contexts/DownloadManagerContext";
import { WhitelistSection } from "./WhitelistSection";
import { AdminTabSwitch } from "../../../components/AdminTabSwitch";
import CandidateDetailDrawer from "./CandidateDetailDrawer.tsx";
import type { Candidate, OnboardingResponse, OnboardingStats } from "../../../types/onboarding.ts";
import { getToken, getUser } from "../../../lib/auth.ts";
import { useApiRequest } from "../../../hooks/useApiRequest.ts";
import { useInfiniteSectionScroll } from "../../../hooks/useInfiniteSectionScroll.ts";
import { useSectionPage } from "../../../hooks/useSectionPage.ts";
import { settings } from "../../../shared/settings.ts";
import { MapaCandidatosTab } from "./components/MapaCandidatosTab";
import type { StatKey } from "./components/StatsGrid";
import { SelectedCandidatesPanel } from "./components/SelectedCandidatesPanel";
import type { KanbanColumnData } from "./components/KanbanColumn";
import type { SectionGroup } from "./components/CandidateSectionAccordion";
import type { ViewMode } from "./components/ViewToggle";
import { getCurrentWeekRange, toYmd } from "../../../lib/weekRange";
import { WeekFilterChip } from "./components/WeekFilterChip";

const CANDIDATES_KANBAN_PAGE_SIZE = 20;
const CANDIDATES_LIST_PAGE_SIZE = 3;
const CANDIDATES_LIST_PAGE_SIZE_OPTIONS = [3, 10, 30, 50];

const EMPTY_STATS: OnboardingStats = { total: 0, em_andamento: 0, aguardando: 0, aguardando_rh: 0, concluidos: 0, cancelados: 0 };

interface CandidateFilters {
  sector: string | string[];
  date: { startDate: Date; endDate: Date; key: string } | null;
  status: StatKey[];
  /** Data de conclusão do onboarding (mission_maps.completed_at no starter-pack). */
  concluded_at: { startDate: Date; endDate: Date; key: string } | null;
}

const DEFAULT_FILTERS: CandidateFilters = {
  sector: [],
  date: null,
  status: [],
  concluded_at: null,
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "aguardando",    label: "Não Iniciado"  },
  { value: "em_andamento",  label: "Em andamento"  },
  { value: "aguardando_rh", label: "Aguardando RH" },
  { value: "concluidos",    label: "Concluídos"    },
  { value: "cancelados",    label: "Cancelados"    },
];

interface WhitelistSideFilters {
  filterTag: string;
  sortOrder: string;
}

const DEFAULT_WHITELIST_FILTERS: WhitelistSideFilters = {
  filterTag: "all",
  sortOrder: "newest",
};

const WHITELIST_FILTER_FIELDS: FilterField[] = [
  {
    key: "filterTag",
    label: "Classificação",
    type: "select",
    options: [
      { value: "all",  label: "Todos" },
      { value: "pj",   label: "PJ"   },
      { value: "tech", label: "Tech"  },
    ],
  },
  {
    key: "sortOrder",
    label: "Ordenar por",
    type: "select",
    options: [
      { value: "newest", label: "Mais recente" },
      { value: "oldest", label: "Mais antigo"  },
    ],
  },
];

const OnboardingDashboardPage = () => {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get("search") ?? "");
  const [filters, setFilters] = useState<CandidateFilters>(DEFAULT_FILTERS);
  // Filtro "Apenas esta semana" aplicado por padrão às seções Concluídos e Cancelados.
  const [concluidoWeekOnly, setConcluidoWeekOnly] = useState(true);
  const [canceladoWeekOnly, setCanceladoWeekOnly] = useState(true);
  const weekRange = useMemo(() => getCurrentWeekRange(), []);
  const [sectorOptions, setSectorOptions] = useState<{ value: string; label: string }[]>([]);
  const [sectorFieldType, setSectorFieldType] = useState<string>("text");
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string | number>>(new Set());
  const [activeTab, setActiveTab] = useState(0);
  const [whitelistFilters, setWhitelistFilters] = useState<WhitelistSideFilters>(DEFAULT_WHITELIST_FILTERS);
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [listPageSize, setListPageSize] = useState<number>(CANDIDATES_LIST_PAGE_SIZE);
  const handleListPageSizeChange = useCallback((size: number) => setListPageSize(size), []);

  const handleToggleSelect = useCallback((candidateId: string | number) => {
    setSelectedCandidateIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  }, []);
  const handleClearSelection = useCallback(() => setSelectedCandidateIds(new Set()), []);

  const handleRemoveFromSelection = useCallback((id: string | number) => {
    setSelectedCandidateIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Clicar num big number filtra só por aquele status; clicar de novo no
  // mesmo card limpa o filtro — mesmo estado usado pelo filtro lateral.
  const handleStatusCardClick = useCallback((key: StatKey) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status.length === 1 && prev.status[0] === key ? [] : [key],
    }));
  }, []);

  const { request: candidatesStatsReq, data: candidatesStatsRaw, isLoading: isLoadingCandidatesStats } = useApiRequest();
  const candidatesStats = (candidatesStatsRaw as OnboardingStats | null) ?? null;

  const { request: sectorOptionsReq } = useApiRequest();
  const [pendingCancelCandidate, setPendingCancelCandidate] = useState<Candidate | null>(null);
  const { isOpen: isCancelConfirmOpen, onOpen: openCancelConfirm, onClose: closeCancelConfirm } = useDisclosure();
  const cancelConfirmRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const { startDownload } = useDownloadManager();

  const token = getToken() ?? "";

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (activeTab === 1) return;
    sectorOptionsReq({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/field-options/setor`,
      method: "GET",
      skipToast: true,
    }).then((data: any) => {
      if (data) {
        setSectorFieldType(data.field_type ?? "text");
        setSectorOptions(data.options ?? []);
      }
    }).catch(() => {});
  }, [activeTab]);

  // Filtro de conclusão (concluded_from/concluded_to) é aplicado APENAS à
  // seção Concluídos — não vai no buildParams compartilhado pra não esvaziar as
  // demais seções (não têm completed_at). Por padrão = semana atual (chip
  // ligado); com o chip desligado, usa o filtro manual "Data de conclusão".
  const concluidoFrom = concluidoWeekOnly
    ? toYmd(weekRange.startDate)
    : (filters.concluded_at?.startDate ? new Date(filters.concluded_at.startDate).toISOString().slice(0, 10) : undefined);
  const concluidoTo = concluidoWeekOnly
    ? toYmd(weekRange.endDate)
    : (filters.concluded_at?.endDate ? new Date(filters.concluded_at.endDate).toISOString().slice(0, 10) : undefined);

  // Filtro de cancelamento (cancelled_from/cancelled_to) é aplicado APENAS à
  // seção Cancelados, mesmo padrão do filtro de conclusão acima. Por padrão =
  // semana atual (chip ligado).
  const canceladoFrom = canceladoWeekOnly ? toYmd(weekRange.startDate) : undefined;
  const canceladoTo = canceladoWeekOnly ? toYmd(weekRange.endDate) : undefined;

  const buildParams = useCallback((extra?: Record<string, string>) => {
    const params: Record<string, string> = { ...extra };
    if (debouncedSearch) params.search = debouncedSearch;
    const sector = Array.isArray(filters.sector) ? filters.sector.join(",") : filters.sector;
    if (sector)          params.sector  = sector;
    if (filters.date?.startDate) params.date_from = new Date(filters.date.startDate).toISOString().slice(0, 10);
    if (filters.date?.endDate)   params.date_to   = new Date(filters.date.endDate).toISOString().slice(0, 10);
    return params;
  }, [debouncedSearch, filters]);

  const fetchCandidatesStats = useCallback(() => {
    candidatesStatsReq({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/onboarding/candidates/stats`,
      method: "GET",
      skipToast: true,
    }).catch(() => {});
  }, [candidatesStatsReq]);

  useEffect(() => { if (activeTab === 0) fetchCandidatesStats(); }, [activeTab, fetchCandidatesStats]);

  const { request: fetchAguardandoRhReq } = useApiRequest();
  const { request: fetchEmAndamentoReq } = useApiRequest();
  const { request: fetchAguardandoReq } = useApiRequest();
  const { request: fetchConcluidosReq } = useApiRequest();
  const { request: fetchCanceladosReq } = useApiRequest();

  const fetchAguardandoRhBase = useCallback(async (page: number, pageSize: number) => {
    const res = await fetchAguardandoRhReq({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/onboarding/candidates`,
      method: "GET",
      params: { ...buildParams(), status: "AGUARDANDO_RH", page: String(page), page_size: String(pageSize) },
      skipToast: true,
    }) as OnboardingResponse;
    return { candidates: res.candidates, total: res.total, total_pages: res.total_pages };
  }, [fetchAguardandoRhReq, buildParams]);

  const fetchEmAndamentoBase = useCallback(async (page: number, pageSize: number) => {
    const res = await fetchEmAndamentoReq({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/onboarding/candidates`,
      method: "GET",
      params: { ...buildParams(), status: "EM_ANDAMENTO", page: String(page), page_size: String(pageSize) },
      skipToast: true,
    }) as OnboardingResponse;
    return { candidates: res.candidates, total: res.total, total_pages: res.total_pages };
  }, [fetchEmAndamentoReq, buildParams]);

  const fetchAguardandoBase = useCallback(async (page: number, pageSize: number) => {
    const res = await fetchAguardandoReq({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/onboarding/candidates`,
      method: "GET",
      params: { ...buildParams(), status: "AGUARDANDO", page: String(page), page_size: String(pageSize) },
      skipToast: true,
    }) as OnboardingResponse;
    return { candidates: res.candidates, total: res.total, total_pages: res.total_pages };
  }, [fetchAguardandoReq, buildParams]);

  const fetchConcluidosBase = useCallback(async (page: number, pageSize: number) => {
    const params: Record<string, string> = { ...buildParams(), status: "CONCLUIDO", page: String(page), page_size: String(pageSize) };
    if (concluidoFrom) params.concluded_from = concluidoFrom;
    if (concluidoTo) params.concluded_to = concluidoTo;
    const res = await fetchConcluidosReq({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/onboarding/candidates`,
      method: "GET",
      params,
      skipToast: true,
    }) as OnboardingResponse;
    return { candidates: res.candidates, total: res.total, total_pages: res.total_pages };
  }, [fetchConcluidosReq, buildParams, concluidoFrom, concluidoTo]);

  const fetchCanceladosBase = useCallback(async (page: number, pageSize: number) => {
    const params: Record<string, string> = { ...buildParams(), status: "CANCELADO", page: String(page), page_size: String(pageSize) };
    if (canceladoFrom) params.cancelled_from = canceladoFrom;
    if (canceladoTo) params.cancelled_to = canceladoTo;
    const res = await fetchCanceladosReq({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/onboarding/candidates`,
      method: "GET",
      params,
      skipToast: true,
    }) as OnboardingResponse;
    return { candidates: res.candidates, total: res.total, total_pages: res.total_pages };
  }, [fetchCanceladosReq, buildParams, canceladoFrom, canceladoTo]);

  const fetchAguardandoRhKanban = useCallback((page: number) => fetchAguardandoRhBase(page, CANDIDATES_KANBAN_PAGE_SIZE), [fetchAguardandoRhBase]);
  const fetchEmAndamentoKanban  = useCallback((page: number) => fetchEmAndamentoBase(page, CANDIDATES_KANBAN_PAGE_SIZE),  [fetchEmAndamentoBase]);
  const fetchAguardandoKanban   = useCallback((page: number) => fetchAguardandoBase(page, CANDIDATES_KANBAN_PAGE_SIZE),   [fetchAguardandoBase]);
  const fetchConcluidosKanban   = useCallback((page: number) => fetchConcluidosBase(page, CANDIDATES_KANBAN_PAGE_SIZE),   [fetchConcluidosBase]);
  const fetchCanceladosKanban   = useCallback((page: number) => fetchCanceladosBase(page, CANDIDATES_KANBAN_PAGE_SIZE),   [fetchCanceladosBase]);

  const fetchAguardandoRhList = useCallback((page: number) => fetchAguardandoRhBase(page, listPageSize), [fetchAguardandoRhBase, listPageSize]);
  const fetchEmAndamentoList  = useCallback((page: number) => fetchEmAndamentoBase(page, listPageSize),  [fetchEmAndamentoBase, listPageSize]);
  const fetchAguardandoList   = useCallback((page: number) => fetchAguardandoBase(page, listPageSize),   [fetchAguardandoBase, listPageSize]);
  const fetchConcluidosList   = useCallback((page: number) => fetchConcluidosBase(page, listPageSize),   [fetchConcluidosBase, listPageSize]);
  const fetchCanceladosList   = useCallback((page: number) => fetchCanceladosBase(page, listPageSize),   [fetchCanceladosBase, listPageSize]);

  const candidatesTabEnabled = activeTab === 0;
  const kanbanEnabled = candidatesTabEnabled && viewMode === "kanban";
  const visibleStatuses = filters.status;
  const isStatusVisible = useCallback(
    (key: StatKey) => visibleStatuses.length === 0 || visibleStatuses.includes(key),
    [visibleStatuses],
  );
  const aguardandoRhKanban = useInfiniteSectionScroll(fetchAguardandoRhKanban, kanbanEnabled && isStatusVisible("aguardando_rh"));
  const emAndamentoKanban  = useInfiniteSectionScroll(fetchEmAndamentoKanban,  kanbanEnabled && isStatusVisible("em_andamento"));
  const aguardandoKanban   = useInfiniteSectionScroll(fetchAguardandoKanban,   kanbanEnabled && isStatusVisible("aguardando"));
  const concluidosKanban   = useInfiniteSectionScroll(fetchConcluidosKanban,   kanbanEnabled && isStatusVisible("concluidos"));
  const canceladosKanban   = useInfiniteSectionScroll(fetchCanceladosKanban,   kanbanEnabled && isStatusVisible("cancelados"));

  const [activatedCandidateSections, setActivatedCandidateSections] = useState<Set<string>>(() => new Set(["aguardando_rh"]));
  const handleCandidateSectionExpand = useCallback((key: string) => {
    setActivatedCandidateSections((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);
  const listEnabled = candidatesTabEnabled && viewMode === "lista";
  const aguardandoRhList = useSectionPage(fetchAguardandoRhList, [debouncedSearch, filters], listEnabled && activatedCandidateSections.has("aguardando_rh") && isStatusVisible("aguardando_rh"));
  const emAndamentoList  = useSectionPage(fetchEmAndamentoList,  [debouncedSearch, filters], listEnabled && activatedCandidateSections.has("em_andamento")  && isStatusVisible("em_andamento"));
  const aguardandoList   = useSectionPage(fetchAguardandoList,   [debouncedSearch, filters], listEnabled && activatedCandidateSections.has("aguardando")    && isStatusVisible("aguardando"));
  const concluidosList   = useSectionPage(fetchConcluidosList,   [debouncedSearch, filters, concluidoWeekOnly], listEnabled && activatedCandidateSections.has("concluidos")    && isStatusVisible("concluidos"));
  const canceladosList   = useSectionPage(fetchCanceladosList,   [debouncedSearch, filters, canceladoWeekOnly], listEnabled && activatedCandidateSections.has("cancelados")    && isStatusVisible("cancelados"));

  const kanbanSections = [aguardandoRhKanban, emAndamentoKanban, aguardandoKanban, concluidosKanban, canceladosKanban];
  const listSections = [aguardandoRhList, emAndamentoList, aguardandoList, concluidosList, canceladosList];
  const error = viewMode === "kanban" ? kanbanSections.some((s) => s.error) : listSections.some((s) => s.error);

  const retryCandidatesSections = useCallback(() => {
    aguardandoRhKanban.refetch(); emAndamentoKanban.refetch(); aguardandoKanban.refetch(); concluidosKanban.refetch(); canceladosKanban.refetch();
    aguardandoRhList.refetch(); emAndamentoList.refetch(); aguardandoList.refetch(); concluidosList.refetch(); canceladosList.refetch();
  }, [
    aguardandoRhKanban, emAndamentoKanban, aguardandoKanban, concluidosKanban, canceladosKanban,
    aguardandoRhList, emAndamentoList, aguardandoList, concluidosList, canceladosList,
  ]);

  const candidates: Candidate[] = viewMode === "kanban"
    ? [...aguardandoRhKanban.candidates, ...emAndamentoKanban.candidates, ...aguardandoKanban.candidates, ...concluidosKanban.candidates, ...canceladosKanban.candidates]
    : [...aguardandoRhList.candidates, ...emAndamentoList.candidates, ...aguardandoList.candidates, ...concluidosList.candidates, ...canceladosList.candidates];

  const candidatesById = useMemo(
    () => new Map(candidates.map((c) => [c.user_id, c])),
    [candidates],
  );
  const selectedCandidates = useMemo(
    () =>
      Array.from(selectedCandidateIds).map((id) => {
        const c = candidatesById.get(id as number);
        return c
          ? { user_id: c.user_id, name: c.name, email: c.email }
          : { user_id: id, name: `Candidato #${id}`, email: "" };
      }),
    [selectedCandidateIds, candidatesById],
  );

  const handleSelectAll = useCallback(() => {
    if (selectedCandidateIds.size === candidates.length && candidates.length > 0) {
      setSelectedCandidateIds(new Set());
    } else {
      setSelectedCandidateIds(new Set(candidates.map(c => c.user_id)));
    }
  }, [candidates, selectedCandidateIds.size]);

  const handleExport = useCallback(async () => {
    try {
      const exportParams = buildParams();
      if (selectedCandidateIds.size > 0) {
        exportParams.user_ids = Array.from(selectedCandidateIds).join(",");
      }
      // Quem pediu — usado pelo gg-hub para validar a permissão de salário.
      const user = getUser();
      if (user?.email) exportParams.requested_by = user.email;
      const label = selectedCandidateIds.size > 0
        ? `Excel (${selectedCandidateIds.size} selecionado${selectedCandidateIds.size !== 1 ? "s" : ""})`
        : "Exportar Excel";
      await startDownload(
        exportParams,
        label,
        settings.environment.VITE_STARTER_PACK_BASE_URL ?? "",
        "onboarding/candidates/export/xlsx/jobs",
      );
    } catch {
      toast({ title: "Erro ao iniciar exportação Excel", status: "error", duration: 4000, isClosable: true });
    }
  }, [buildParams, selectedCandidateIds, startDownload, toast]);

  const handleDownloadFiles = useCallback(async () => {
    try {
      const params: Record<string, string | string[]> = buildParams();
      if (selectedCandidateIds.size === 0) {
        toast({
          title: "Nenhum candidato selecionado",
          description: "Selecione pelo menos 1 candidato ou use 'Selecionar todos' abaixo.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      params.user_ids = Array.from(selectedCandidateIds).map(String);
      await startDownload(
        params,
        `Documentos (${selectedCandidateIds.size} selecionados)`,
        settings.environment.VITE_STARTER_PACK_BASE_URL ?? "",
      );
      handleClearSelection();
    } catch {
      toast({
        title: "Erro ao iniciar download",
        description: "Não foi possível criar o job de exportação.",
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    }
  }, [buildParams, selectedCandidateIds, startDownload, toast, handleClearSelection]);

  const { request: cancelReq } = useApiRequest();

  const handleCancelCandidate = useCallback((candidate: Candidate) => {
    setPendingCancelCandidate(candidate);
    openCancelConfirm();
  }, [openCancelConfirm]);

  const handleConfirmCancel = useCallback(async () => {
    if (!pendingCancelCandidate) return;
    closeCancelConfirm();
    try {
      await cancelReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(pendingCancelCandidate.email)}/cancel`,
        method: "POST",
        skipToast: true,
      });
      toast({ title: `${pendingCancelCandidate.name} marcado como desistente`, status: "warning", duration: 4000, isClosable: true });
      retryCandidatesSections();
      fetchCandidatesStats();
    } catch {
      toast({ title: "Erro ao cancelar candidato", status: "error", duration: 4000, isClosable: true });
    } finally {
      setPendingCancelCandidate(null);
    }
  }, [pendingCancelCandidate, cancelReq, retryCandidatesSections, fetchCandidatesStats, toast, closeCancelConfirm]);

  const handleReactivateCandidate = useCallback(async (candidate: Candidate) => {
    try {
      await cancelReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(candidate.email)}/reactivate`,
        method: "POST",
        skipToast: true,
      });
      toast({ title: `${candidate.name} reativado com sucesso`, status: "success", duration: 4000, isClosable: true });
      retryCandidatesSections();
      fetchCandidatesStats();
    } catch {
      toast({ title: "Erro ao reativar candidato", status: "error", duration: 4000, isClosable: true });
    }
  }, [cancelReq, retryCandidatesSections, fetchCandidatesStats, toast]);

  const stats = candidatesStats ?? EMPTY_STATS;

  const allColumns: KanbanColumnData[] = [
    {
      key: "aguardando", label: "Não Iniciado", dot: "yellow.400",
      candidates: aguardandoKanban.candidates, total: aguardandoKanban.total,
      hasMore: aguardandoKanban.hasMore, onLoadMore: aguardandoKanban.loadMore,
      isLoadingInitial: aguardandoKanban.isLoadingInitial, isLoadingMore: aguardandoKanban.isLoadingMore,
      hasFetched: aguardandoKanban.hasFetched,
    },
    {
      key: "em_andamento", label: "Em andamento", dot: "blue.400",
      candidates: emAndamentoKanban.candidates, total: emAndamentoKanban.total,
      hasMore: emAndamentoKanban.hasMore, onLoadMore: emAndamentoKanban.loadMore,
      isLoadingInitial: emAndamentoKanban.isLoadingInitial, isLoadingMore: emAndamentoKanban.isLoadingMore,
      hasFetched: emAndamentoKanban.hasFetched,
    },
    {
      key: "aguardando_rh", label: "Aguardando RH", dot: "orange.400",
      candidates: aguardandoRhKanban.candidates, total: aguardandoRhKanban.total,
      hasMore: aguardandoRhKanban.hasMore, onLoadMore: aguardandoRhKanban.loadMore,
      isLoadingInitial: aguardandoRhKanban.isLoadingInitial, isLoadingMore: aguardandoRhKanban.isLoadingMore,
      hasFetched: aguardandoRhKanban.hasFetched,
    },
    {
      key: "concluidos", label: "Concluídos", dot: "green.400",
      candidates: concluidosKanban.candidates, total: concluidosKanban.total,
      hasMore: concluidosKanban.hasMore, onLoadMore: concluidosKanban.loadMore,
      isLoadingInitial: concluidosKanban.isLoadingInitial, isLoadingMore: concluidosKanban.isLoadingMore,
      hasFetched: concluidosKanban.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={concluidoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setConcluidoWeekOnly((v) => !v)}
          activeTitle="Mostrar todos os concluídos"
          inactiveTitle="Filtrar apenas os concluídos nesta semana"
        />
      ),
    },
    {
      key: "cancelados", label: "Cancelados", dot: "red.400",
      candidates: canceladosKanban.candidates, total: canceladosKanban.total,
      hasMore: canceladosKanban.hasMore, onLoadMore: canceladosKanban.loadMore,
      isLoadingInitial: canceladosKanban.isLoadingInitial, isLoadingMore: canceladosKanban.isLoadingMore,
      hasFetched: canceladosKanban.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={canceladoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setCanceladoWeekOnly((v) => !v)}
          label="Cancelado essa semana"
          activeTitle="Mostrar todos os cancelados"
          inactiveTitle="Filtrar apenas os cancelados nesta semana"
        />
      ),
    },
  ];
  const columns = visibleStatuses.length === 0
    ? allColumns
    : visibleStatuses
        .map((key) => allColumns.find((c) => c.key === key))
        .filter((c): c is KanbanColumnData => Boolean(c));

  const allSectionGroups: SectionGroup[] = [
    {
      key: "aguardando", label: "Não Iniciado", dot: "yellow.400",
      candidates: aguardandoList.candidates,
      page: aguardandoList.page, totalPages: aguardandoList.totalPages,
      onPageChange: aguardandoList.setPage, isLoading: aguardandoList.isLoading,
      pageSize: listPageSize, pageSizeOptions: CANDIDATES_LIST_PAGE_SIZE_OPTIONS, onPageSizeChange: handleListPageSizeChange,
      hasFetched: aguardandoList.hasFetched,
    },
    {
      key: "em_andamento", label: "Em andamento", dot: "blue.400",
      candidates: emAndamentoList.candidates,
      page: emAndamentoList.page, totalPages: emAndamentoList.totalPages,
      onPageChange: emAndamentoList.setPage, isLoading: emAndamentoList.isLoading,
      pageSize: listPageSize, pageSizeOptions: CANDIDATES_LIST_PAGE_SIZE_OPTIONS, onPageSizeChange: handleListPageSizeChange,
      hasFetched: emAndamentoList.hasFetched,
    },
    {
      key: "aguardando_rh", label: "Aguardando RH", dot: "orange.400",
      candidates: aguardandoRhList.candidates,
      page: aguardandoRhList.page, totalPages: aguardandoRhList.totalPages,
      onPageChange: aguardandoRhList.setPage, isLoading: aguardandoRhList.isLoading,
      pageSize: listPageSize, pageSizeOptions: CANDIDATES_LIST_PAGE_SIZE_OPTIONS, onPageSizeChange: handleListPageSizeChange,
      hasFetched: aguardandoRhList.hasFetched,
    },
    {
      key: "concluidos", label: "Concluídos", dot: "green.400",
      candidates: concluidosList.candidates,
      page: concluidosList.page, totalPages: concluidosList.totalPages,
      onPageChange: concluidosList.setPage, isLoading: concluidosList.isLoading,
      pageSize: listPageSize, pageSizeOptions: CANDIDATES_LIST_PAGE_SIZE_OPTIONS, onPageSizeChange: handleListPageSizeChange,
      hasFetched: concluidosList.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={concluidoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setConcluidoWeekOnly((v) => !v)}
          activeTitle="Mostrar todos os concluídos"
          inactiveTitle="Filtrar apenas os concluídos nesta semana"
        />
      ),
    },
    {
      key: "cancelados", label: "Cancelados", dot: "red.400",
      candidates: canceladosList.candidates,
      page: canceladosList.page, totalPages: canceladosList.totalPages,
      onPageChange: canceladosList.setPage, isLoading: canceladosList.isLoading,
      pageSize: listPageSize, pageSizeOptions: CANDIDATES_LIST_PAGE_SIZE_OPTIONS, onPageSizeChange: handleListPageSizeChange,
      hasFetched: canceladosList.hasFetched,
      headerExtra: (
        <WeekFilterChip
          active={canceladoWeekOnly}
          weekStart={weekRange.startDate}
          weekEnd={weekRange.endDate}
          onToggle={() => setCanceladoWeekOnly((v) => !v)}
          label="Cancelado essa semana"
          activeTitle="Mostrar todos os cancelados"
          inactiveTitle="Filtrar apenas os cancelados nesta semana"
        />
      ),
    },
  ];
  const sectionGroups = visibleStatuses.length === 0
    ? allSectionGroups
    : visibleStatuses
        .map((key) => allSectionGroups.find((g) => g.key === key))
        .filter((g): g is SectionGroup => Boolean(g));

  const isMultiSector = (sectorFieldType === "select" || sectorFieldType === "multi_select") && sectorOptions.length > 0;
  const filterFields: FilterField[] = [
    { key: "status", label: "Status", type: "multiSelect", options: STATUS_OPTIONS },
    ...(isMultiSector
      ? [{ key: "sector", label: "Setor", type: "multiSelect" as const, options: sectorOptions }]
      : [{ key: "sector", label: "Setor", type: "text" as const, placeholder: "Filtrar por setor..." }]),
    { key: "date", label: "Data de início", type: "dateRange" },
    { key: "concluded_at", label: "Data de conclusão da trilha", type: "dateRange" },
  ];

  return (
    <Box display="flex" justifyContent="flex-start" alignItems="flex-start" minH="100vh">
      <Box display={{ base: "none", md: "flex" }}>
        {activeTab === 1 ? (
          <SideFilter<WhitelistSideFilters>
            filters={whitelistFilters}
            onChange={setWhitelistFilters}
            onReset={() => setWhitelistFilters(DEFAULT_WHITELIST_FILTERS)}
            fields={WHITELIST_FILTER_FIELDS}
            panelWidth={300}
          />
        ) : (
          <SideFilter<CandidateFilters>
            filters={filters}
            onChange={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
            fields={filterFields}
            panelWidth={300}
          />
        )}
      </Box>

      <Box flex="1" minW={0}>
        <Box maxW="container.xl" mx="auto" p={{ base: 4, md: 6 }}>
          <AdminTabSwitch index={activeTab} onChange={setActiveTab} tabs={[
            { label: "Mapa Candidatos", content: (
              <MapaCandidatosTab
                stats={stats} isLoading={isLoadingCandidatesStats} error={error}
                search={search} onSearchChange={setSearch}
                selectedCandidateIds={selectedCandidateIds}
                onToggleSelect={handleToggleSelect} onSelectAll={handleSelectAll}
                candidates={candidates} columns={columns} sectionGroups={sectionGroups}
                viewMode={viewMode} onViewModeChange={setViewMode}
                onSectionExpand={handleCandidateSectionExpand}
                statusFilter={filters.status} onStatusFilterToggle={handleStatusCardClick}
                onViewDetail={(c) => setSelectedCandidate(c)}
                onCancel={handleCancelCandidate} onReactivate={handleReactivateCandidate}
                onDownload={handleDownloadFiles} onExport={handleExport}
                onRetry={retryCandidatesSections}
              />
            )},
            { label: "Whitelist", content: <WhitelistSection token={token} filterTag={whitelistFilters.filterTag as "all" | "pj" | "tech"} sortOrder={whitelistFilters.sortOrder as "newest" | "oldest"} /> },
          ]} />

          <CandidateDetailDrawer
            candidate={selectedCandidate}
            isOpen={!!selectedCandidate}
            onClose={() => setSelectedCandidate(null)}
            onRefresh={retryCandidatesSections}
            mode="geral"
          />

          <AlertDialog
            isOpen={isCancelConfirmOpen}
            leastDestructiveRef={cancelConfirmRef}
            onClose={closeCancelConfirm}
            isCentered
          >
            <AlertDialogOverlay backdropFilter="blur(4px)" />
            <AlertDialogContent borderRadius="xl">
              <AlertDialogHeader fontSize="md" fontWeight={700}>
                Marcar como desistente
              </AlertDialogHeader>
              <AlertDialogBody fontSize="sm">
                Tem certeza que deseja marcar{" "}
                <strong>{pendingCancelCandidate?.name ?? pendingCancelCandidate?.email}</strong>{" "}
                como desistente? Essa ação pode ser revertida depois.
              </AlertDialogBody>
              <AlertDialogFooter gap={2}>
                <Button ref={cancelConfirmRef} variant="ghost" onClick={closeCancelConfirm}>
                  Voltar
                </Button>
                <Button colorScheme="red" onClick={handleConfirmCancel}>
                  Confirmar desistência
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Box>
      </Box>

      {activeTab === 0 && (
        <SelectedCandidatesPanel
          selectedCandidates={selectedCandidates}
          onRemove={handleRemoveFromSelection}
        />
      )}
    </Box>
  );
};

export default OnboardingDashboardPage;